import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RecommendationDto } from "@green0meter/api-client";
import { isEmpty } from "lodash";
import { Link } from "react-router-dom";

import { useUserStore } from "@store/user";
import { useRecommendationsApi } from "@api";
import {
  Button,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@components/base";
import {
  getRecommendationLanguage,
  RecommendationListItem,
} from "@components/main/recommendation";

interface PersonalizedRecommendationsCard {}

const PersonalizedRecommendationsCard: FC<PersonalizedRecommendationsCard> = () => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { isLoadingRecommendations, recommendations, recommendationsEnabled } =
    useRecommendationsApi(user);

  const topRecommendations: RecommendationDto[] = useMemo(() => {
    if (!recommendations) return [];

    const active = recommendations.filter((r) => !r.isCompleted && !r.isNotRelevant);

    return active.slice(0, 3);
  }, [recommendations]);

  const isLoading = isLoadingRecommendations;
  const isEmptyData = !isLoading && isEmpty(topRecommendations);

  if (!recommendationsEnabled || (!isLoading && isEmptyData)) {
    return null;
  }

  return (
    <Card id="manager-recommendations-card">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        p={3}
        pb={0}
      >
        <Typography variant="subtitle1">
          {t("randomRecommendations.title")}
        </Typography>

        <Link
          to="/co2/dashboard"
          state={{ scrollTo: "recommendations" }}
          style={{ textDecoration: "none" }}
        >
          <Button variant="outlined" size="small">
            {t("common.viewAll")}
          </Button>
        </Link>
      </Stack>
      <CardContent>
        <Grid container spacing={2}>
          {isLoading
            ? Array(3)
                .fill(null)
                .map((_, idx) => (
                  <Grid size={12} key={`skeleton-${idx}`}>
                    <Skeleton variant="rectangular" height={220} />
                  </Grid>
                ))
            : topRecommendations.map((recommendation) => {
                const translation = recommendation.translations.find(
                  (t) => t.languages_code === getRecommendationLanguage(),
                );

                if (!translation) return null;

                return (
                  <Grid
                    size={{ lg: 4, md: 4, sm: 12 }}
                    key={recommendation.external_id}
                  >
                    <RecommendationListItem
                      translation={translation}
                      recommendation={recommendation}
                    />
                  </Grid>
                );
              })}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default PersonalizedRecommendationsCard;
